import { apiSlice } from './apiSlice'

export interface Memorial {
  id: string
  case_id: string
  team_id: string
  file_url: string
  file_name: string
  file_size: number
  title?: string
  version?: number
  submitted_at?: string
  status: string
  storage_path?: string
  tags?: string[]
  force_submitted?: boolean
  force_submitted_by?: string
  force_submitted_reason?: string
  submission_locked?: boolean
  is_official_submission?: boolean
}

export interface MemorialVersionSummary {
  memorial_id: string
  case_id: string
  team_id: string
  version: number
  title?: string
  file_name: string
  file_size: number
  submitted_at?: string
  status: string
  file_url?: string
  is_official_submission: boolean
}

export interface MemorialDeadlineStatus {
  case_id: string
  submission_open: boolean
  message: string
  case_deadline?: string
  round_memorial_deadline?: string
  round_id?: string
  round_opens_at?: string
  round_closes_at?: string
  server_now_iso?: string
  timezone?: string
}

export interface MemorialSubmissionLockState {
  locked: boolean
  case_id: string
  team_id: string
  official_memorial_id?: string
  locked_at?: string
  locked_by_user_id?: string
}

export interface MemorialCreate {
  case_id: string
  team_id: string
  file: File
  title?: string
  version?: number
}

export const memorialsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getMemorials: builder.query<Memorial[], { case_id?: string; team_id?: string }>({
      query: (params) => ({
        url: '/memorials/list',
        params,
      }),
      providesTags: ['Memorial'],
    }),
    getMemorial: builder.query<Memorial, string>({
      query: (id) => `/memorials/${id}`,
      providesTags: (result, error, id) => [{ type: 'Memorial', id }],
    }),
    getMemorialVersions: builder.query<
      MemorialVersionSummary[],
      { case_id: string; team_id: string }
    >({
      query: (params) => ({
        url: '/memorials/versions',
        params,
      }),
      providesTags: ['Memorial'],
    }),
    getMemorialDeadlineStatus: builder.query<MemorialDeadlineStatus, string>({
      query: (case_id) => ({
        url: '/memorials/deadline-status',
        params: { case_id },
      }),
    }),
    getMemorialLockStatus: builder.query<
      MemorialSubmissionLockState,
      { case_id: string; team_id: string }
    >({
      query: (params) => ({
        url: '/memorials/lock-status',
        params,
      }),
      providesTags: ['Memorial'],
    }),
    submitMemorial: builder.mutation<Memorial, FormData>({
      query: (formData) => ({
        url: '/memorials/create',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Memorial'],
    }),
    officialSubmitMemorial: builder.mutation<MemorialSubmissionLockState, string>({
      query: (memorialId) => ({
        url: `/memorials/${memorialId}/official-submit`,
        method: 'POST',
      }),
      invalidatesTags: ['Memorial'],
    }),
    releaseMemorialSubmissionLock: builder.mutation<
      { message: string },
      { case_id: string; team_id: string; reason?: string }
    >({
      query: ({ case_id, team_id, reason }) => ({
        url: '/memorials/admin/submission-lock',
        method: 'DELETE',
        params: { case_id, team_id, ...(reason ? { reason } : {}) },
      }),
      invalidatesTags: ['Memorial'],
    }),
    downloadMemorial: builder.query<Blob, string>({
      query: (id) => ({
        url: `/memorials/${id}/download`,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    deleteMemorial: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/memorials/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Memorial'],
    }),
  }),
})

export const {
  useGetMemorialsQuery,
  useGetMemorialQuery,
  useGetMemorialVersionsQuery,
  useLazyGetMemorialVersionsQuery,
  useGetMemorialDeadlineStatusQuery,
  useGetMemorialLockStatusQuery,
  useSubmitMemorialMutation,
  useOfficialSubmitMemorialMutation,
  useReleaseMemorialSubmissionLockMutation,
  useLazyDownloadMemorialQuery,
  useDeleteMemorialMutation,
} = memorialsApi
